import { useState } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ScrollToTop from "@/components/feature/ScrollToTop";
import Breadcrumb from "@/components/base/Breadcrumb";
import { testimonials } from "@/mocks/featured";

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [formMessage, setFormMessage] = useState("");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus("loading");
    setFormMessage("");

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get("website_alt") as string;
    if (honeypot && honeypot.trim() !== "") {
      setFormStatus("success");
      setFormMessage("感谢您的留言，我们会尽快回复！");
      form.reset();
      return;
    }

    try {
      const response = await fetch("https://readdy.ai/api/form/d9u3op78l97n3eas3gq0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(
          Array.from(formData.entries())
            .filter(([key]) => key !== "website_alt")
            .map(([key, value]) => [key, value.toString()])
        ).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = { message: responseText };
      }

      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;

      if (
        response.ok &&
        parsed?.code === "OK"
      ) {
        setFormStatus("success");
        setFormMessage("感谢您的留言，我们会尽快回复！");
        form.reset();
      } else {
        setFormStatus("error");
        setFormMessage(
          serverMsg.includes("spam")
            ? "提交失败，请稍后重试。"
            : serverMsg || "提交失败，请稍后重试。"
        );
      }
    } catch {
      setFormStatus("error");
      setFormMessage("网络错误，请检查连接后重试。");
    }
  };

  const aboutSchema = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    name: "关于我们 - 第一次指南",
    description: "了解「第一次指南」的品牌故事、使命愿景和团队信念。我们致力于帮助每一位用户从紧张到自信地迎接人生中的每一个「第一次」。",
    url: "https://nervousnix.github.io/about",
    isPartOf: { "@type": "WebSite", name: "第一次指南", url: "https://nervousnix.github.io" },
  };

  const orgSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "第一次指南",
    url: "https://nervousnix.github.io",
    description: "专注于「第一次」经验分享的中文内容平台，帮助每一位用户从紧张到自信地迎接人生中的全新体验。",
    foundingDate: "2025",
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "customer service",
      email: "hello@diyiciguide.com",
      availableLanguage: ["Chinese"],
    },
  };

  const testimonialAvatars = [
    "https://readdy.ai/api/search-image?query=A%20warm%20professional%20portrait%20of%20a%20young%20Chinese%20woman%20with%20confident%20smile%2C%20soft%20natural%20lighting%2C%20cream%20background%2C%20editorial%20portrait%20photography&width=200&height=200&seq=testimonial-avatar-01&orientation=squarish",
    "https://readdy.ai/api/search-image?query=A%20warm%20casual%20portrait%20of%20a%20young%20Chinese%20man%20with%20relaxed%20smile%2C%20soft%20natural%20lighting%2C%20cream%20background%2C%20editorial%20portrait%20photography&width=200&height=200&seq=testimonial-avatar-02&orientation=squarish",
    "https://readdy.ai/api/search-image?query=A%20warm%20portrait%20of%20a%20young%20Chinese%20student%20woman%20with%20bright%20smile%2C%20soft%20natural%20lighting%2C%20cream%20background%2C%20editorial%20portrait%20photography&width=200&height=200&seq=testimonial-avatar-03&orientation=squarish",
  ];

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(aboutSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>

      <Navbar />
      <main className="pt-16">
        <div className="bg-background-100">
          <div className="max-w-7xl mx-auto px-6 md:px-10 py-8">
            <Breadcrumb
              items={[
                { label: "首页", href: "/" },
                { label: "关于我们" },
              ]}
            />
          </div>
        </div>

        <section className="py-16 md:py-20 bg-background-50">
          <div className="max-w-4xl mx-auto px-6 md:px-10">
            <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-6 text-center">
              关于「第一次指南」
            </h1>

            <div className="mt-10 space-y-8">
              <div>
                <h2 className="font-heading text-xl font-semibold text-foreground-950 mb-4">
                  我们的故事
                </h2>
                <p className="text-foreground-700 leading-relaxed mb-3">
                  「第一次指南」诞生于一个简单的观察：每个人的成长路上，都充满了各种各样的「第一次」。第一次面试、第一次约会、第一次演讲、第一次独居……这些看似普通的时刻，往往承载着我们最深的焦虑和最亮的成长。
                </p>
                <p className="text-foreground-700 leading-relaxed mb-3">
                  我们的创始团队来自心理学、教育学和内容创作领域，深知「第一次」背后的紧张与期待。我们希望打造一个温暖而有深度的内容平台，让每一个面对未知的你，都能在这里找到实用的指导和真诚的陪伴。
                </p>
                <p className="text-foreground-700 leading-relaxed">
                  从第一篇面试攻略开始，我们已累计发布了超过200篇原创文章，覆盖职场、情感、生活、学习、社交、旅行六大核心场景。每一篇文章都经过精心撰写和多轮审核，确保信息准确、建议实用、态度温暖。
                </p>
              </div>

              <div>
                <h2 className="font-heading text-xl font-semibold text-foreground-950 mb-4">
                  我们的使命
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                  {[
                    { icon: "ri-lightbulb-line", title: "知识赋能", desc: "用系统化的内容，为每一个「第一次」提供可执行的行动指南" },
                    { icon: "ri-heart-pulse-line", title: "心理陪伴", desc: "传递温暖的理解与支持，让你知道自己并不孤单" },
                    { icon: "ri-community-line", title: "社群共建", desc: "连接有过相似经历的人，在分享中获得勇气和力量" },
                  ].map((item) => (
                    <div key={item.title} className="text-center p-6 rounded-xl bg-background-100 border border-background-200/70">
                      <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3 rounded-full bg-primary-100">
                        <i className={`${item.icon} text-xl text-primary-600`}></i>
                      </div>
                      <h3 className="font-heading text-base font-semibold text-foreground-950 mb-2">
                        {item.title}
                      </h3>
                      <p className="text-sm text-foreground-600 leading-relaxed">
                        {item.desc}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 md:py-20 bg-background-100">
          <div className="max-w-4xl mx-auto px-6 md:px-10">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-10 text-center">
              读者口碑
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {testimonials.map((t, idx) => (
                <div key={t.id} className="bg-background-50 rounded-xl border border-background-200/70 p-6 flex flex-col">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-11 h-11 rounded-full overflow-hidden flex-shrink-0">
                      <img
                        src={testimonialAvatars[idx]}
                        alt={t.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">{t.name}</p>
                      <p className="text-xs text-foreground-500">{t.role}</p>
                    </div>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed flex-1">
                    &ldquo;{t.content}&rdquo;
                  </p>
                  <div className="flex items-center gap-0.5 mt-4">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <i key={star} className="ri-star-fill text-accent-500 text-xs"></i>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 md:py-20 bg-background-50">
          <div className="max-w-2xl mx-auto px-6 md:px-10">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3 text-center">
              联系我们
            </h2>
            <p className="text-foreground-600 text-center mb-10 leading-relaxed">
              有任何问题、建议或合作意向，欢迎给我们留言
            </p>

            <form
              onSubmit={handleSubmit}
              data-readdy-form="true"
              id="contact-form"
              className="space-y-5 bg-background-100 rounded-xl border border-background-200/70 p-6 md:p-8"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-800 mb-1.5">
                    姓名
                  </label>
                  <input
                    type="text"
                    id="contact-name"
                    name="name"
                    required
                    className="w-full px-4 py-2.5 rounded-lg border border-background-300/60 bg-background-50 text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                    placeholder="你的名字"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-800 mb-1.5">
                    邮箱
                  </label>
                  <input
                    type="email"
                    id="contact-email"
                    name="email"
                    required
                    className="w-full px-4 py-2.5 rounded-lg border border-background-300/60 bg-background-50 text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                    placeholder="your@email.com"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-800 mb-1.5">
                  主题
                </label>
                <input
                  type="text"
                  id="contact-subject"
                  name="subject"
                  required
                  className="w-full px-4 py-2.5 rounded-lg border border-background-300/60 bg-background-50 text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                  placeholder="留言主题"
                />
              </div>

              <div className="spam-filter-field">
                <input
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
              </div>

              <div>
                <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-800 mb-1.5">
                  留言内容
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  required
                  maxLength={500}
                  rows={5}
                  className="w-full px-4 py-2.5 rounded-lg border border-background-300/60 bg-background-50 text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                  placeholder="请输入你的留言（最多500字）"
                ></textarea>
                <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
              </div>

              {formMessage && (
                <div
                  className={`p-4 rounded-lg text-sm ${
                    formStatus === "success"
                      ? "bg-accent-100 text-accent-800"
                      : "bg-red-100 text-red-800"
                  }`}
                >
                  {formMessage}
                </div>
              )}

              <button
                type="submit"
                disabled={formStatus === "loading"}
                className="w-full sm:w-auto px-8 py-3 bg-primary-500 hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed text-background-50 font-medium rounded-full transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                {formStatus === "loading" ? (
                  <>
                    <i className="ri-loader-4-line animate-spin mr-2"></i>
                    提交中...
                  </>
                ) : (
                  <>
                    发送留言
                    <i className="ri-send-plane-line ml-2"></i>
                  </>
                )}
              </button>
            </form>

            <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-5 text-center">
              <div className="p-5 rounded-xl bg-background-100 border border-background-200/70">
                <div className="w-10 h-10 flex items-center justify-center mx-auto mb-3 rounded-full bg-primary-100">
                  <i className="ri-mail-line text-primary-600 text-lg"></i>
                </div>
                <p className="text-xs text-foreground-500 mb-1">电子邮箱</p>
                <p className="text-sm font-medium text-foreground-900">hello@diyiciguide.com</p>
              </div>
              <div className="p-5 rounded-xl bg-background-100 border border-background-200/70">
                <div className="w-10 h-10 flex items-center justify-center mx-auto mb-3 rounded-full bg-accent-100">
                  <i className="ri-chat-3-line text-accent-600 text-lg"></i>
                </div>
                <p className="text-xs text-foreground-500 mb-1">微信公众号</p>
                <p className="text-sm font-medium text-foreground-900">第一次指南</p>
              </div>
              <div className="p-5 rounded-xl bg-background-100 border border-background-200/70">
                <div className="w-10 h-10 flex items-center justify-center mx-auto mb-3 rounded-full bg-secondary-100">
                  <i className="ri-timer-line text-secondary-600 text-lg"></i>
                </div>
                <p className="text-xs text-foreground-500 mb-1">回复时效</p>
                <p className="text-sm font-medium text-foreground-900">24小时内回复</p>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}